import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence, useReducedMotion, useInView } from "framer-motion";
import { Accessibility, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal } from "@/lib/reveal";
import { SectionMarker } from "@/components/SectionMarker";
import { SCROLL_CONTEXTS } from "@/data/content";

const R = 38; // orbit radius (% from centre)
const coord = (angleDeg) => {
  const a = (angleDeg * Math.PI) / 180;
  return { x: 50 + Math.cos(a) * R, y: 50 + Math.sin(a) * R };
};

/* A-ID at centre with journey icons orbiting. `active` = index. Unchanged visual. */
const Orbit = ({ active, reduce }) => (
  <div className="relative mx-auto aspect-square w-full max-w-[min(86vw,500px)]" aria-hidden="true">
    <div className="absolute inset-0 radial-glow scale-110" />
    <div className="absolute inset-[15%] rounded-full border border-white/10" />
    <div className="absolute inset-[32%] rounded-full border border-teal/15" />

    <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full">
      {SCROLL_CONTEXTS.map((c, i) => {
        const { x, y } = coord(c.angle);
        const on = i === active;
        return (
          <line
            key={c.id} x1="50" y1="50" x2={x} y2={y}
            stroke={on ? c.color : "rgba(148,163,184,0.18)"}
            strokeWidth={on ? 1 : 0.4}
            strokeDasharray="1.4 1.6"
            className={on && !reduce ? "dashed-path" : ""}
            style={{ transition: "stroke 0.5s ease, stroke-width 0.5s ease" }}
          />
        );
      })}
    </svg>

    {SCROLL_CONTEXTS.map((c, i) => {
      const { x, y } = coord(c.angle);
      const on = i === active;
      return (
        <div
          key={c.id}
          className="absolute transition-all duration-500"
          style={{ left: `${x}%`, top: `${y}%`, transform: `translate(-50%,-50%) scale(${on ? 1.16 : 1})`, opacity: on ? 1 : 0.4 }}
        >
          {on && !reduce && (
            <span className="absolute inset-0 rounded-full animate-pulse-ring" style={{ background: `${c.color}55` }} />
          )}
          <div
            className="relative flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-full glass transition-all duration-500"
            style={{ borderColor: on ? c.color : "rgba(255,255,255,0.08)", boxShadow: on ? `0 0 34px -4px ${c.color}` : "none" }}
            title={c.label}
          >
            <c.Icon className="h-5 w-5 sm:h-6 sm:w-6 transition-colors duration-500" style={{ color: on ? c.color : "#94A3B8" }} />
          </div>
        </div>
      );
    })}

    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
      <div className="relative flex h-24 w-24 sm:h-28 sm:w-28 flex-col items-center justify-center rounded-full bg-gradient-to-br from-navy-600 to-navy-900 ring-2 ring-cyan/40 shadow-[0_0_48px_-10px_rgba(0,240,255,0.6)]">
        <Accessibility className="h-8 w-8 sm:h-9 sm:w-9 text-cyan" />
        <span className="mt-0.5 font-display text-xs sm:text-sm font-extrabold tracking-[0.16em] text-white">A-ID</span>
      </div>
    </div>
  </div>
);

const slide = {
  enter: (d) => ({ opacity: 0, x: d > 0 ? 44 : -44 }),
  center: { opacity: 1, x: 0 },
  exit: (d) => ({ opacity: 0, x: d > 0 ? -44 : 44 }),
};

export const AidScrollExperience = () => {
  const reduce = useReducedMotion();
  const len = SCROLL_CONTEXTS.length;
  const [active, setActive] = useState(0);
  const [dir, setDir] = useState(1);
  const [interacted, setInteracted] = useState(false);

  // Gentle one-time autoplay when the explorer first scrolls into view.
  const viewRef = useRef(null);
  const inView = useInView(viewRef, { amount: 0.4 });
  const autoRef = useRef(null);
  const activeRef = useRef(0);
  const [autoDone, setAutoDone] = useState(false);

  useEffect(() => { activeRef.current = active; }, [active]);

  const stopAuto = () => {
    if (autoRef.current) { clearInterval(autoRef.current); autoRef.current = null; }
  };

  useEffect(() => {
    if (reduce || !inView || autoDone) return;
    setDir(1);
    autoRef.current = setInterval(() => {
      const cur = activeRef.current;
      if (cur >= len - 1) { stopAuto(); setAutoDone(true); return; }
      setActive(cur + 1);
    }, 2100);
    return stopAuto;
  }, [inView, reduce, autoDone, len]);

  const goTo = (i) => {
    stopAuto();
    setAutoDone(true);
    const next = Math.min(len - 1, Math.max(0, i));
    if (next === active) return;
    setDir(next > active ? 1 : -1);
    setActive(next);
    setInteracted(true);
  };
  const step = (delta) => goTo(active + delta);

  // Whole-section swipe / click-drag (touch + mouse). Vertical scroll preserved via touch-action: pan-y.
  const swipeStart = useRef(null);
  const onSwipeDown = (e) => { swipeStart.current = { x: e.clientX, y: e.clientY }; };
  const onSwipeUp = (e) => {
    const s = swipeStart.current;
    swipeStart.current = null;
    if (!s) return;
    const dx = e.clientX - s.x;
    const dy = e.clientY - s.y;
    if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.2) step(dx < 0 ? 1 : -1);
  };

  const ctx = SCROLL_CONTEXTS[active];

  return (
    <section id="how" className="relative py-20 lg:py-28 overflow-hidden" data-testid="aid-scroll-section">
      {/* subtle per-journey tint */}
      <motion.div
        className="pointer-events-none absolute inset-0 -z-10"
        animate={{ background: `radial-gradient(50% 50% at 40% 45%, ${ctx.color}1f, rgba(5,11,20,0) 70%)` }}
        transition={{ duration: reduce ? 0 : 0.6, ease: "easeInOut" }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl">
          <SectionMarker n="03" label="How A-ID works" />
          <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
            How A-ID Works
          </h2>
          <p className="mt-4 font-display text-xl sm:text-2xl font-semibold text-white">
            One person. One accessibility identity. Many journeys.
          </p>
          <p className="mt-4 text-lg text-slate-300 leading-relaxed">
            Your accessibility needs don’t stay in one place — they move with you. A-ID brings the
            right information into context for each journey, helping you communicate what you need
            with greater confidence, choice and control.
          </p>
        </Reveal>

        {/* screen-reader accessible list of all journeys */}
        <ul className="sr-only">
          {SCROLL_CONTEXTS.map((c) => (
            <li key={c.id}>
              {c.label}: {c.examples.map((ex) => `${ex.title} — ${ex.detail}`).join(" ")}
            </li>
          ))}
        </ul>

        <div
          ref={viewRef}
          className="mt-12 lg:mt-14 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center select-none"
          style={{ touchAction: "pan-y" }}
          onPointerDown={onSwipeDown}
          onPointerUp={onSwipeUp}
          onPointerCancel={() => (swipeStart.current = null)}
        >
          {/* orbit visual */}
          <div className="order-1 cursor-grab active:cursor-grabbing">
            <Orbit active={active} reduce={reduce} />
          </div>

          {/* horizontal journey explorer */}
          <div
            className="order-2 select-none"
            role="group"
            aria-roledescription="carousel"
            aria-label="A-ID accessibility journeys"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "ArrowRight") { e.preventDefault(); step(1); }
              if (e.key === "ArrowLeft") { e.preventDefault(); step(-1); }
            }}
            data-testid="journey-explorer"
          >
            {/* swipe hint */}
            <div
              className="mb-4 flex items-center gap-2 text-sm text-slatemuted transition-opacity duration-500"
              style={{ opacity: interacted ? 0 : 1 }}
              aria-hidden="true"
            >
              <span>Swipe to explore how A-ID adapts to your journey</span>
              <ChevronRight className="h-4 w-4 text-teal animate-pulse" />
            </div>

            {/* swipeable content region (whole section also swipes via the grid handlers) */}
            <div className="relative min-h-[220px] cursor-grab active:cursor-grabbing" aria-live="polite">
              <AnimatePresence custom={dir} mode="wait" initial={false}>
                <motion.div
                  key={ctx.id}
                  custom={dir}
                  variants={slide}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: reduce ? 0 : 0.34, ease: [0.22, 1, 0.36, 1] }}
                >
                  <span className="inline-flex items-center gap-2.5 text-sm font-mono uppercase tracking-[0.22em]" style={{ color: ctx.color }}>
                    <ctx.Icon className="h-5 w-5" aria-hidden="true" /> {ctx.label}
                  </span>
                  <h3
                    className="mt-3 font-display font-extrabold tracking-tight leading-[1] text-white"
                    style={{ fontSize: "clamp(1.9rem, 4.5vw, 3.75rem)" }}
                  >
                    {ctx.label}
                  </h3>
                  <div className="mt-4 h-1 w-14 rounded-full" style={{ background: ctx.color }} />
                  <ul className="mt-5 max-w-md space-y-3.5" data-testid="journey-examples">
                    {ctx.examples.map((ex) => (
                      <li key={ex.title} className="flex gap-3">
                        <span
                          className="mt-[7px] h-2 w-2 shrink-0 rounded-full"
                          style={{ background: ctx.color, boxShadow: `0 0 10px -1px ${ctx.color}` }}
                          aria-hidden="true"
                        />
                        <span>
                          <span className="block font-display text-base font-semibold text-white leading-snug">
                            {ex.title}
                          </span>
                          <span className="block text-sm text-slate-300 leading-relaxed">
                            {ex.detail}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* controls: prev · dots · next */}
            <div className="mt-8 flex items-center gap-4">
              <button
                type="button"
                onClick={() => step(-1)}
                disabled={active === 0}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white transition-colors hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                aria-label="Previous journey"
                data-testid="journey-prev"
              >
                <ChevronLeft className="h-5 w-5" aria-hidden="true" />
              </button>

              <div className="flex flex-1 items-center justify-center gap-2" role="tablist" aria-label="Choose a journey">
                {SCROLL_CONTEXTS.map((c, i) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => goTo(i)}
                    role="tab"
                    aria-selected={i === active}
                    aria-label={c.label}
                    className="group flex h-6 items-center cursor-pointer"
                    data-testid={`journey-dot-${c.id}`}
                  >
                    <span
                      className="rounded-full transition-all duration-300"
                      style={{
                        width: i === active ? 26 : 9,
                        height: 9,
                        background: i === active ? c.color : "rgba(148,163,184,0.35)",
                      }}
                    />
                  </button>
                ))}
              </div>

              <button
                type="button"
                onClick={() => step(1)}
                disabled={active === len - 1}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white transition-colors hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                aria-label="Next journey"
                data-testid="journey-next"
              >
                <ChevronRight className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            <p className="mt-4 text-xs font-mono uppercase tracking-[0.2em] text-slatemuted">
              {String(active + 1).padStart(2, "0")} / {String(len).padStart(2, "0")} · One identity · Many journeys · Your control
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
